import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { exportToCSV, exportToExcel } from '../utils/exportUtils';

function DataTable({
  data,
  columns,
  onEdit,
  onDelete,
  customActions,
  getRowClass,
  hideActions,
  hideEdit,
  hideDelete,
  itemsPerPage = 25,
  disableSorting = false,
  toolbarContent = null,
  toolbarInset = false,
  serverSide = false,
  page = 1,
  pageSize,
  totalItems,
  searchValue = '',
  onSearchChange,
  sortField: controlledSortField = '',
  sortDirection: controlledSortDirection = 'asc',
  onSortChange,
  onPageChange,
  loading = false,
  getExportData,
  exportFileName = 'data',
  tableClassName = ''
}) {
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filter, setFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [actionMenuPosition, setActionMenuPosition] = useState({ top: 0, left: 0 });
  const [exporting, setExporting] = useState(false);

  const activePageSize = serverSide ? (pageSize || itemsPerPage) : itemsPerPage;
  const activeCurrentPage = serverSide ? page : currentPage;
  const activeSortField = serverSide ? controlledSortField : sortField;
  const activeSortDirection = serverSide ? controlledSortDirection : sortDirection;
  const activeFilter = serverSide ? searchValue : filter;

  useEffect(() => {
    const handleOutsideClick = () => setOpenActionMenu(null);
    if (openActionMenu !== null) {
      document.addEventListener('mousedown', handleOutsideClick);
      return () => document.removeEventListener('mousedown', handleOutsideClick);
    }
  }, [openActionMenu]);

  const filteredData = useMemo(() => {
    if (serverSide) return data;

    return data.filter(item => {
      // Skip group headers in search
      if (item.isGroup) return true;
      
      return Object.entries(item).some(([key, value]) => {
        // Skip searching in nested objects and arrays
        if (typeof value === 'object' && value !== null) {
          // For nested objects, search in their string properties
          return Object.values(value).some(nestedVal => 
            nestedVal?.toString().toLowerCase().includes(filter.toLowerCase())
          );
        }
        return value?.toString().toLowerCase().includes(filter.toLowerCase());
      });
    });
  }, [data, filter, serverSide]);

  const sortedData = useMemo(() => {
    if (serverSide) return data;
    if (!sortField) return filteredData;
    return [...filteredData].sort((a, b) => {
      const column = columns.find(col => col.field === sortField);
      let aVal, bVal;
      
      if (column && column.render) {
        // For rendered columns, get the raw value for sorting
        aVal = column.sortValue ? column.sortValue(a[sortField], a) : a[sortField];
        bVal = column.sortValue ? column.sortValue(b[sortField], b) : b[sortField];
      } else {
        // Handle nested object properties
        aVal = sortField.includes('.') ? 
          sortField.split('.').reduce((obj, key) => obj?.[key], a) : a[sortField];
        bVal = sortField.includes('.') ? 
          sortField.split('.').reduce((obj, key) => obj?.[key], b) : b[sortField];
      }
      
      // Handle null/undefined values
      if (aVal == null && bVal == null) return 0;
      if (aVal == null) return sortDirection === 'asc' ? 1 : -1;
      if (bVal == null) return sortDirection === 'asc' ? -1 : 1;
      
      // Convert to string for comparison if needed
      if (typeof aVal === 'string') aVal = aVal.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal.toLowerCase();
      
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [filteredData, sortField, sortDirection, columns, data, serverSide]);

  const paginatedData = useMemo(() => {
    if (serverSide) return data;
    const startIndex = (currentPage - 1) * itemsPerPage;
    return sortedData.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedData, currentPage, data, serverSide, itemsPerPage]);

  const renderedData = serverSide ? data : paginatedData;
  const totalRows = serverSide ? (typeof totalItems === 'number' ? totalItems : data.length) : sortedData.length;
  const totalPages = Math.max(1, Math.ceil(totalRows / activePageSize));
  const visibleCount = renderedData.length;
  const actionColumnCount = (onEdit || onDelete || customActions) ? 1 : 0;
  const skeletonColumnCount = columns.length + actionColumnCount;
  const skeletonRowCount = Math.min(5, Math.max(3, activePageSize > 0 ? activePageSize : 5));

  const handleSort = (field) => {
    if (disableSorting) return;
    const nextDirection = activeSortField === field && activeSortDirection === 'asc' ? 'desc' : 'asc';

    if (serverSide) {
      onSortChange?.(field, nextDirection);
      onPageChange?.(1);
    } else {
      if (sortField === field) {
        setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
      } else {
        setSortField(field);
        setSortDirection('asc');
      }
    }
  };

  const handlePageChange = (nextPage) => {
    const safePage = Math.min(Math.max(1, nextPage), totalPages);
    if (serverSide) {
      onPageChange?.(safePage);
      return;
    }

    setCurrentPage(safePage);
  };

  const handleSearchInputChange = (value) => {
    if (serverSide) {
      onSearchChange?.(value);
      return;
    }

    setFilter(value);
    setCurrentPage(1);
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const exportRows = getExportData
        ? await getExportData()
        : (serverSide ? data : sortedData);

      if (format === 'csv') {
        exportToCSV(exportRows, exportFileName, columns);
      } else {
        exportToExcel(exportRows, exportFileName, columns);
      }
    } finally {
      setExporting(false);
    }
  };

  const handleActionMenuToggle = (event, menuKey) => {
    event.stopPropagation();

    if (openActionMenu === menuKey) {
      setOpenActionMenu(null);
      return;
    }

    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 220;
    const viewportPadding = 8;
    const preferredHeight = 280;
    const spaceBelow = window.innerHeight - rect.bottom - viewportPadding;
    const openAbove = spaceBelow < 180;
    const maxHeight = openAbove
      ? Math.max(140, rect.top - (viewportPadding * 2))
      : Math.max(140, window.innerHeight - rect.bottom - (viewportPadding * 2));

    setActionMenuPosition({
      top: openAbove
        ? Math.max(viewportPadding, rect.top - Math.min(preferredHeight, maxHeight))
        : Math.min(window.innerHeight - viewportPadding, rect.bottom + 4),
      left: Math.min(
        window.innerWidth - menuWidth - viewportPadding,
        Math.max(viewportPadding, rect.right - menuWidth)
      ),
      minWidth: menuWidth,
      maxHeight
    });
    setOpenActionMenu(menuKey);
  };

  return (
    <div style={{overflow: 'visible'}}>
      <div className={toolbarInset ? 'px-3 pt-3' : ''}>
        <div className="row mb-4 g-2 align-items-center">
          <div className={toolbarContent ? 'col-12 col-xl-4 d-flex align-items-center' : 'col-md-6 d-flex align-items-center'}>
            <div className="search-box w-100">
              <input
                type="text"
                className="form-control form-control-sm"
                placeholder="Search across all fields..."
                value={activeFilter}
                onChange={(e) => handleSearchInputChange(e.target.value)}
              />
            </div>
          </div>
          {toolbarContent && (
            <div className="col-12 col-xl d-flex align-items-center data-table-toolbar">
              {toolbarContent}
            </div>
          )}
          <div className={toolbarContent ? 'col-12 col-xl-auto d-flex align-items-center ms-xl-auto' : 'col-md-6 d-flex align-items-center'}>
            <div className="d-flex align-items-center justify-content-end gap-2 h-100 flex-wrap ms-auto">
            <div className="dropdown">
              <button className="btn btn-outline-secondary btn-sm dropdown-toggle" data-bs-toggle="dropdown" disabled={exporting}>
                <i className="fas fa-download me-1"></i>Export
              </button>
              <ul className="dropdown-menu">
                <li><button className="dropdown-item" onClick={() => handleExport('csv')}>
                  <i className="fas fa-file-csv me-2"></i>CSV
                </button></li>
                <li><button className="dropdown-item" onClick={() => handleExport('excel')}>
                  <i className="fas fa-file-excel me-2"></i>Excel
                </button></li>
              </ul>
            </div>
          </div>
          </div>
        </div>
      </div>

      <div className="table-responsive d-none d-md-block" style={{overflow: 'visible'}}>
        <table className={`table table-striped ${tableClassName}`.trim()} style={{overflow: 'visible'}}>
          <thead>
            <tr>
              {columns.map((column) => (
                <th
                  key={column.field}
                  onClick={() => column.field !== 'checkbox' && handleSort(column.sortField || column.field)}
                  style={{ cursor: disableSorting || column.field === 'checkbox' ? 'default' : 'pointer' }}
                >
                  {column.headerSubLabel ? (
                    <div className="d-flex align-items-center justify-content-between gap-3">
                      <span className="text-nowrap">
                        {column.label}
                        {!disableSorting && column.field !== 'checkbox' && activeSortField === (column.sortField || column.field) && (
                          <i className={`fas fa-sort-${activeSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                        )}
                      </span>
                      <small className="text-muted fw-normal text-nowrap">{column.headerSubLabel}</small>
                    </div>
                  ) : (
                    <>
                      {column.label}
                      {!disableSorting && column.field !== 'checkbox' && activeSortField === (column.sortField || column.field) && (
                        <i className={`fas fa-sort-${activeSortDirection === 'asc' ? 'up' : 'down'} ms-1`}></i>
                      )}
                    </>
                  )}
                </th>
              ))}
              {(onEdit || onDelete || customActions) && <th>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              Array.from({ length: skeletonRowCount }, (_, rowIndex) => (
                <tr key={`skeleton-row-${rowIndex}`} className="table-skeleton-row" aria-hidden="true">
                  {Array.from({ length: skeletonColumnCount }, (_, colIndex) => (
                    <td key={`skeleton-cell-${rowIndex}-${colIndex}`}>
                      <div
                        className={`table-skeleton-line ${colIndex === 0 ? 'table-skeleton-line-short' : ''}`}
                      ></div>
                    </td>
                  ))}
                </tr>
              ))
            ) : renderedData.length === 0 ? (
              <tr>
                <td colSpan={skeletonColumnCount} className="text-center py-4">
                  <div className="text-muted">
                    <i className="fas fa-inbox fa-2x mb-2"></i>
                    <p>No data available</p>
                  </div>
                </td>
              </tr>
            ) : (
              renderedData.map((item, index) => (
                <tr 
                  key={index} 
                  className={`${getRowClass ? getRowClass(item) : ''} ${selectedRow === index ? 'table-active' : ''}`}
                  onClick={() => setSelectedRow(index)}
                >
                  {columns.map((column, colIndex) => {
                    if (item.isGroup && colIndex > 0) return null;
                    const cellClassName = typeof column.cellClassName === 'function'
                      ? column.cellClassName(item[column.field], item)
                      : column.cellClassName;
                    return (
                      <td 
                        key={column.field} 
                        className={cellClassName || undefined}
                        style={{userSelect: 'text'}}
                        colSpan={item.isGroup ? columns.length + ((onEdit || onDelete || customActions) ? 1 : 0) : 1}
                      >
                        {column.render ? column.render(item[column.field], item) : item[column.field]}
                      </td>
                    );
                  })}
                  {(onEdit || onDelete || customActions) && (
                    <td onClick={(e) => e.stopPropagation()}>
                      {!hideActions?.(item) && (
                        <div>
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={(e) => {
                              handleActionMenuToggle(e, `desktop-${index}`);
                            }}
                          >
                            <i className="fas fa-ellipsis-v"></i>
                          </button>
                          {openActionMenu === `desktop-${index}` && (
                            createPortal(
                              <ul
                                className="dropdown-menu show"
                                style={{
                                  position: 'fixed',
                                  top: actionMenuPosition.top,
                                  left: actionMenuPosition.left,
                                  minWidth: actionMenuPosition.minWidth,
                                  maxHeight: actionMenuPosition.maxHeight,
                                  overflowY: 'auto',
                                  zIndex: 3000
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {onEdit && !hideEdit?.(item) && (
                                  <li>
                                    <button className="dropdown-item" onClick={() => { setOpenActionMenu(null); onEdit(item); }}>
                                      <i className="fas fa-edit me-2"></i>Edit
                                    </button>
                                  </li>
                                )}
                                {customActions && customActions(item, () => setOpenActionMenu(null))}
                                {onDelete && !hideDelete?.(item) && (
                                  <>
                                    {((onEdit && !hideEdit?.(item)) || customActions) && <li><hr className="dropdown-divider" /></li>}
                                    <li>
                                      <button className="dropdown-item text-danger" onClick={() => { setOpenActionMenu(null); onDelete(item.id); }}>
                                        <i className="fas fa-trash me-2"></i>Delete
                                      </button>
                                    </li>
                                  </>
                                )}
                              </ul>,
                              document.body
                            )
                          )}
                        </div>
                      )}
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="d-md-none">
        {loading ? (
          <div className="data-card-skeleton-list" aria-hidden="true">
            {Array.from({ length: 3 }, (_, index) => (
              <div key={`mobile-skeleton-${index}`} className="card mb-3 shadow-sm">
                <div className="card-body">
                  <div className="table-skeleton-line table-skeleton-line-medium mb-3"></div>
                  <div className="table-skeleton-line mb-2"></div>
                  <div className="table-skeleton-line table-skeleton-line-short mb-2"></div>
                  <div className="table-skeleton-line table-skeleton-line-medium"></div>
                </div>
              </div>
            ))}
          </div>
        ) : renderedData.length === 0 ? (
          <div className="text-center py-5">
            <div className="text-muted">
              <i className="fas fa-inbox fa-3x mb-3"></i>
              <p>No data available</p>
            </div>
          </div>
        ) : (
          renderedData.map((item, index) => {
            if (item.isGroup) {
              const column = columns.find(col => col.render);
              const renderedContent = column ? column.render(item[column.field], item) : null;
              return (
                <div key={index} className="card mb-2 bg-light border-primary" style={{cursor: 'pointer'}} onClick={(e) => {
                  if (renderedContent?.props?.onClick) {
                    renderedContent.props.onClick(e);
                  }
                }}>
                  <div className="card-body py-2">
                    {renderedContent || (
                      <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                          <i className="fas fa-user me-2 text-primary"></i>
                          <strong>{item.employeeName}</strong>
                        </div>
                        <span className="badge bg-primary">{item.assignments?.length || 0}</span>
                      </div>
                    )}
                  </div>
                </div>
              );
            }
            const rowClass = getRowClass ? getRowClass(item) : '';
            const cardClass = rowClass.includes('table-') ? rowClass.replace('table-', 'border-') : '';
            return (
              <div key={index} className={`card mb-3 shadow-sm ${cardClass}`}>
                <div className="card-body">
                  {columns.filter(col => col.field !== 'checkbox').map((column) => {
                    const value = column.render ? column.render(item[column.field], item) : item[column.field];
                    if (value === '' || value === null || value === undefined) return null;
                    return (
                      <div key={column.field} className="row mb-2">
                        <div className="col-5">
                          <strong className="text-muted">{column.label}:</strong>
                        </div>
                        <div className="col-7">
                          {value}
                        </div>
                      </div>
                    );
                  })}
                  {(onEdit || onDelete || customActions) && (
                    <div className="row mt-3">
                      <div className="col-12">
                        {!hideActions?.(item) && (
                          <div>
                            <button
                              className="btn btn-sm btn-outline-secondary"
                              onClick={(e) => {
                                handleActionMenuToggle(e, `mobile-${index}`);
                              }}
                            >
                              <i className="fas fa-ellipsis-v me-1"></i>Actions
                            </button>
                            {openActionMenu === `mobile-${index}` && createPortal(
                              <ul
                                className="dropdown-menu show"
                                style={{
                                  position: 'fixed',
                                  top: actionMenuPosition.top,
                                  left: actionMenuPosition.left,
                                  minWidth: actionMenuPosition.minWidth,
                                  maxHeight: actionMenuPosition.maxHeight,
                                  overflowY: 'auto',
                                  zIndex: 3000
                                }}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => e.stopPropagation()}
                              >
                                {onEdit && !hideEdit?.(item) && (
                                  <li>
                                    <button className="dropdown-item" onClick={() => { setOpenActionMenu(null); onEdit(item); }}>
                                      <i className="fas fa-edit me-2"></i>Edit
                                    </button>
                                  </li>
                                )}
                                {customActions && customActions(item, () => setOpenActionMenu(null))}
                                {onDelete && !hideDelete?.(item) && (
                                  <>
                                    {((onEdit && !hideEdit?.(item)) || customActions) && <li><hr className="dropdown-divider" /></li>}
                                    <li>
                                      <button className="dropdown-item text-danger" onClick={() => { setOpenActionMenu(null); onDelete(item.id); }}>
                                        <i className="fas fa-trash me-2"></i>Delete
                                      </button>
                                    </li>
                                  </>
                                )}
                              </ul>,
                              document.body
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="table-footer-bar">
        {totalPages > 1 && (
        <nav className="mb-0">
          <ul className="pagination justify-content-center mb-0">
            <li className={`page-item ${activeCurrentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(1)} disabled={activeCurrentPage === 1}>
                <i className="fas fa-angle-double-left"></i>
              </button>
            </li>
            <li className={`page-item ${activeCurrentPage === 1 ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(activeCurrentPage - 1)} disabled={activeCurrentPage === 1}>
                <i className="fas fa-angle-left"></i>
              </button>
            </li>
            {(() => {
              const maxVisible = 5;
              const halfVisible = Math.floor(maxVisible / 2);
              let startPage = Math.max(1, activeCurrentPage - halfVisible);
              let endPage = Math.min(totalPages, startPage + maxVisible - 1);
              
              if (endPage - startPage < maxVisible - 1) {
                startPage = Math.max(1, endPage - maxVisible + 1);
              }
              
              const pages = [];
              for (let i = startPage; i <= endPage; i++) {
                pages.push(
                  <li key={i} className={`page-item ${activeCurrentPage === i ? 'active' : ''}`}>
                    <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
                  </li>
                );
              }
              return pages;
            })()}
            <li className={`page-item ${activeCurrentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(activeCurrentPage + 1)} disabled={activeCurrentPage === totalPages}>
                <i className="fas fa-angle-right"></i>
              </button>
            </li>
            <li className={`page-item ${activeCurrentPage === totalPages ? 'disabled' : ''}`}>
              <button className="page-link" onClick={() => handlePageChange(totalPages)} disabled={activeCurrentPage === totalPages}>
                <i className="fas fa-angle-double-right"></i>
              </button>
            </li>
          </ul>
        </nav>
        )}
        <div className="table-entry-count">
          <i className="fas fa-list me-1"></i>
          {visibleCount} of {totalRows} entries
        </div>
      </div>
    </div>
  );
}

export default DataTable;
